import React, { useEffect, useRef } from "react";
import { Modal, Button, Input, Textarea } from "../../../components/ui";
import { deleteIcon } from "../../../assets/Icons/icons";
import { strings } from "../../../strings/strings";

const QuestionForm = ({
  onClose,
  isRtl = false,
  data,
  setData,
  handleSave,
  isEditing,
  questionNo,
  hasFormChanged,
  notes,
  setNotes,
  errors,
  isLoading = false,
}) => {
  const optionEndRef = useRef(null);

  useEffect(() => {
    if (optionEndRef.current) {
      optionEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [data.options?.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...(data.options || [])];
    updatedOptions[index] = value;
    setData((prev) => ({ ...prev, options: updatedOptions }));
  };

  const MAX_OPTIONS = 4;

  const handleAddOption = () => {
    if ((data.options || []).length < MAX_OPTIONS) {
      setData((prev) => ({
        ...prev,
        options: [...(prev.options || []), ""],
      }));
    }
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = [...(data.options || [])];
    updatedOptions.splice(index, 1);
    setData((prev) => ({ ...prev, options: updatedOptions }));
  };

  const handleSubmit = () => {
    handleSave();
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={onClose}>
        {strings.cancel}
      </Button>

      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={(isEditing && !hasFormChanged()) || isLoading}
        isLoading={isLoading}
        className={isEditing && !hasFormChanged() ? "opacity-50 cursor-not-allowed" : ""}
      >
        {isEditing ? strings.update : strings.add} {strings.question}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`${isEditing ? strings.edit : strings.add} ${strings.question}`}
      footer={footer}
      size="md"
    >
      <div className="space-y-4 overflow-x-visible pl-2 pr-2 pb-4">
        {errors?.general && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errors.general}
          </div>
        )}

        <div>
          <Input
            label={strings.questionNo}
            type="text"
            value={questionNo || ""}
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        <div>
          <Input
            label={strings.question}
            name="question"
            type="text"
            value={data.question || ""}
            dir={isRtl ? "rtl" : "ltr"}
            onChange={handleChange}
            placeholder={strings.enterYourQuestion}
            required
            error={errors?.question}
            touched={!!errors?.question}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary mb-2">{strings.warning}</label>
          <Textarea
            value={notes || ""}
            dir={isRtl ? "rtl" : "ltr"}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={strings.enterNotesOptional}
            rows={4}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary mb-2">
            {strings.Options} <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2 mb-2">
            {(data.options || []).map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-sm font-medium min-w-[24px] text-secondary">{`${idx + 1}.`}</span>
                <Input
                  type="text"
                  value={option || ""}
                  dir={isRtl ? "rtl" : "ltr"}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  required
                  className="flex-1"
                  error={errors?.options?.[idx]}
                  touched={!!errors?.options?.[idx]}
                />
                {data.options.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(idx)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0 p-1 transition-colors"
                    title={strings.removeOption}
                    aria-label={strings.removeOption}
                  >
                    <img src={deleteIcon} alt={strings.delete} className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {data.options?.length < 4 && (
            <Button type="button" variant="primary" size="sm" onClick={handleAddOption} className="mt-4 text-primary">
              {strings.addoption}
            </Button>
          )}
        </div>
        <div ref={optionEndRef} />
      </div>
    </Modal>
  );
};

export default QuestionForm;
